import { type BlocksContent } from '@strapi/blocks-react-renderer'

import type { StrapiImage } from '@/src/domains/strapi/schemas/image'

import { type AuthorType } from '../onlineLessons/types'

export type ImageAttributes = { width: number; height: number; url: string; alternativeText?: string }

export type StrapiMediaResponse = {
  data: { attributes: ImageAttributes }
}

export type StrapiImageV4 = {
  data?: {
    attributes?: StrapiImage
  }
}

export type PageBlockIteratorItemType = { name: string; data: PageBlockType; isBusinessPage: boolean }

export type StrapiResult = {
  title: string
  arrow: 'increase' | 'decrease'
  value: number
  unitAfter?: string
  unitBefore?: string
  note?: BlocksContent
  description?: BlocksContent
}

export type PageBlockType = {
  id: number
  __component: string
  common?: PageBlockCommonType
  questions?: FAQQuestionType[]
  media?: StrapiMediaResponse
  boxedNote?: BlocksContent
  imagePosition?: 'left' | 'right'
  imageLeft?: any
  imageRight?: any
  animation?: StrapiMediaResponse
  coloredBox?: ColoredBox[]
  insuranceBox?: boolean
  companyLogo?: {
    data: { id: number; attributes: ImageAttributes }[]
  }
  pagesCollection?: {
    // Strapi returns `data: null` when the relation is not set
    data: {
      documentId: string
    } | null
  }
  slide?: SlideType[]
  step?: StepType[]
  title?: string
  note?: BlocksContent
  image?: StrapiMediaResponse
  imageDesktop?: StrapiMediaResponse
  imageMobile?: StrapiMediaResponse
  ctaText?: string
  ctaHref?: string
  showHedepyStats?: boolean
  variant?: 'homepage' | 'psychiatry'
  hedepyPlusLogo?: boolean
  insuranceLogos?: boolean
  notes?: IconWithNote[]
  services?: PriceListType[]
  howItWorks?: HowItWorksType[]
  content?: string
  tableHeader?: {
    title: string
  }[]
  tableRows?: {
    logo: StrapiMediaResponse
    inputField: string
  }[]
  serviceId?: number
  sessionType?: 'ONLINE' | 'OFFLINE'
  sessionDistrictIds?: string
  noFreeSlotsFallback?: { headline?: string; description?: BlocksContent; ctaText?: string; ctaHref?: string }
  noLocalTherapistsFallback?: { headline?: string; description?: BlocksContent; ctaText?: string; ctaHref?: string }
  youtubeVideoId?: string
  headerSize?: 'small' | 'large'
  headerDisplayParts?: {
    insuranceLogos: boolean
    stats: boolean
    psychiatryBanner: boolean
    googleReviews: boolean
    googleReviewsLink: string
    hedepyPlusLogo: boolean
    companiesStats: boolean
    customStats: boolean
    results: boolean
    bottomArrow?: boolean
  }
  customStats?: StatsType[]
  testimonials: AuthorType[]
  submitText?: string
  form?: {
    data: {
      attributes: StrapiFormType
    }
  }
  author?: AuthorType
  featureName?: string
  typeformId?: string
  tagList?: TagList[]
  tags: { name: string; articleLink?: string }[]
  list?: ListGridItem[]
  layout?: 'default' | 'large-image' | 'simple' | 'header-on-side'
  imageBubbleText?: string
  results?: StrapiResult[]
  bubbleText?: string
  question?: string
  answer1?: string
  answer2?: string
  person1Mobile?: StrapiMediaResponse
  person1Desktop?: StrapiMediaResponse
  person2Mobile?: StrapiMediaResponse
  person2Desktop?: StrapiMediaResponse
  boxes?: ColoredBox[]
  features?: FeatureType[]
  tableContent?: string[][]
  hideFilters?: boolean
  category?: string
  displayAppButtons?: boolean
  verticalAlign?: boolean
  links?: ButtonListType[]
  formBackground?: string
  items: StrapiGridListItem[]
  logoAboveHeadline?: StrapiMediaResponse
  gaEventName?: string
  benefits?: {
    iconName?: string
    title: string
  }[]
  youtubeCoverImage?: StrapiImageV4
  categories?: ServicesListCategory[]
}
export type StatsType = {
  title: string
  iconName: string
}

export type StrapiGridListItem = { itemName?: string; content?: string; iconName?: string; title?: string }

export type TagList = {
  iconName: string
  iconBgColor: string
  title: string
  tags: {
    name: string
    articleLink: string
  }[]
}

export type PageBlockCommonType = {
  headline?: string
  headlineSize?: PageBlockCommonHeadlineSize
  description?: BlocksContent
  badgeText?: string
  ctaText?: string
  ctaTextMobile?: string
  ctaHref?: string
  secondaryCtaText?: string
  secondaryCtaTextMobile?: string
  secondaryCtaHref?: string
  textColor?: string
  backgroundColor?: string
  sectionAnchor?: string
  bottomSpacing?: boolean
  width?: 'sm' | 'md' | 'lg' | 'xl'
  displayConditions?: PageBlockDisplayConditionsType[]
  bottomArrow?: boolean
}

export enum PageBlockCommonHeadlineSize {
  default = 'default',
  larger = 'larger',
}

export type PageBlockDisplayConditionsType = {
  value: string | boolean
  comparison: 'EQUALS' | 'NOT_EQUALS'
  featureName: string
}

export type HowItWorksType = {
  title: string
  note: BlocksContent
  insuranceTeaser: boolean
  buttonText: string
  buttonLink: string
  image: StrapiMediaResponse
}

export type FAQQuestionType = {
  question?: string
  answer?: string
}

export type StepType = {
  title: string
  content: string
  itemName?: string
}
export type IconWithNote = {
  icon: string
  text: string | BlocksContent | null
  color: string
}

export type PriceListType = {
  serviceName: string
  iconName: string
  lengthInMinutes: number
  insuranceTeaser: boolean
  cards: PriceCardType[]
}

export type PriceCardType = {
  title: string
  note: string
  emphasis: boolean
  discount: string
  pricesDescription: string
  prices: string
  semicolonSeparatedPrices: string
  benefits: string
}

export type ServicesListService = {
  id: number
  iconName?: string
  name: string
  description?: string
  duration?: string
  price?: string
  bookingHref?: string
  moreInfoHref?: string
}

export type ServicesListCategory = {
  id: number
  title: string
  services: ServicesListService[]
}

export type ColoredBox = {
  id: number
  icon?: string
  title?: string
  text?: BlocksContent
  linkText?: string
  linkHref?: string
  color?: string
  isList?: boolean
  image?: StrapiMediaResponse
}

export type SlideType = {
  id: number
  title: string
  imageDesktop: StrapiMediaResponse
  imageMobile: StrapiMediaResponse
}
export type StrapiFormField = {
  label: string
  id: number
  isRequired: true
  placeholder: string
  options?: string
  type: StrapiFormFieldType
  __component: 'form.input' | 'form.options-input' | 'form.textarea' | 'form.checkbox'
}

export type StrapiFormFieldType = 'text' | 'tel' | 'number' | 'email' | 'password'

export type StrapiFormType = {
  name: string
  formFields: StrapiFormField[]
  successTitle?: string
  successDescription?: string
  successImage?: StrapiMediaResponse
  title?: string
  description?: BlocksContent
}

export type ListGridItem = {
  id: number
  title?: string
  content?: string
  iconName?: string
  itemName?: string
}

type ButtonListType = {
  iconName?: string
  withArrow?: boolean
  text: string
  url: string
}

export type FeatureType = {
  title: string
  note: BlocksContent
  image: StrapiMediaResponse
}

export enum CompanyIndustry {
  finance = 'finance',
  manufacturing = 'manufacturing',
  marketing = 'marketing',
  it = 'it',
  insurance = 'insurance',
}

export type TestimonialType = {
  id: number
  authorName?: string
  authorPosition?: string
  authorImage?: StrapiMediaResponse
  companyName?: string
  companyIndustry?: CompanyIndustry
  content: string
  videoId?: string
  featured?: boolean
  companyLogo?: StrapiMediaResponse
  videoCoverImage?: StrapiMediaResponse
}

export type StrapiCaseStudyType = {
  title: string
  slug: string
  perex: BlocksContent
  logo: StrapiMediaResponse
  companyName: string
  companyIndustry: CompanyIndustry
  companySize: string
  content?: ArticleContentType[]
}

// this is a iteratable content that is passed to selectComponent function
// TODO: we should type this and unify the usage in codebase
export type ArticleContentType = any
